import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/api/client";
import CompleteLinksDialog from "@/components/CompleteLinksDialog";
import { useAuth } from "@/context/AuthContext";
import TaskCard from "@/components/TaskCard";
import TaskDialog from "@/components/TaskDialog";
import NotesTodoInline from "@/components/NotesTodoInline";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ListTodo, UserCircle2, Share2, Search, X, Plus } from "lucide-react";
import { EmptyState } from "@/components/EmptyState";

function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function endOfDay(d) { const x = new Date(d); x.setHours(23,59,59,999); return x; }
function startOfWeek(d) {
  const x = startOfDay(d);
  const day = x.getDay();
  const diff = (day + 6) % 7; // Monday start
  x.setDate(x.getDate() - diff);
  return x;
}
function endOfWeek(d) {
  const s = startOfWeek(d); const e = new Date(s); e.setDate(s.getDate() + 6); return endOfDay(e);
}
function startOfMonth(d) { const x = startOfDay(d); x.setDate(1); return x; }
function endOfMonth(d) { const x = startOfMonth(d); x.setMonth(x.getMonth()+1); x.setDate(0); return endOfDay(x); }

function inRange(iso, from, to) {
  if (!iso) return false;
  const dt = new Date(iso);
  return dt >= from && dt <= to;
}

export default function MyTasks() {
  const { user } = useAuth();
  const isManager = user?.role === "manager" || !!user?.is_super_admin || !!user?.is_admin;
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [todos, setTodos] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("mine"); // "mine" | "assigned"
  const [searchQuery, setSearchQuery] = useState("");
  const [completePopupTask, setCompletePopupTask] = useState(null);

  const load = async () => {
    const [t, u, td] = await Promise.all([
      api.get("/tasks"),
      api.get("/users"),
      api.get("/todos"),
    ]);
    setTasks(t.data); setUsers(u.data); setTodos(td.data || []);
  };
  useEffect(() => { load(); }, []);

  // Deadline-tagged personal to-dos become "self-assigned pseudo-tasks" so they
  // show up alongside real work in the bucket views. We stamp `_from_todo: true`
  // so TaskCard-style renderers can distinguish them and skip destructive
  // actions (edit/delete would hit /api/tasks/<not-a-real-id>).
  const todosAsTasks = useMemo(() => {
    if (!user) return [];
    return (todos || [])
      .filter((t) => !!t.deadline)
      .map((t) => ({
        id: `todo-${t.id}`,
        title: t.text,
        client: "Personal",
        description: "",
        assigned_to: user.id,
        assigned_to_name: user.name,
        assignees: [user.id],
        assigned_by: user.id,
        assigned_by_name: user.name,
        // Anchor to 6 PM IST on the deadline day, matching how other self-scheduled work looks.
        deadline: `${t.deadline}T18:00:00`,
        start_date: t.deadline,
        status: t.done ? "complete" : "not_started",
        priority: false,
        _from_todo: true,
        _todo_id: t.id,
        _todo_done: !!t.done,
      }));
  }, [todos, user]);

  // Split source tasks based on the manager's current scope
  const scopedTasks = useMemo(() => {
    if (!user) return [];
    // Match everywhere the user is either the lead OR a co-assignee — same rule
    // the backend uses (server.py:938). Previously MyTasks only checked
    // assigned_to, so shared multi-assignee work silently didn't appear. Also
    // hard-blocks any legacy task where assigned_to points somewhere else but
    // the caller isn't in assignees from ever leaking into their view.
    const belongsToUser = (t) =>
      t.assigned_to === user.id || (Array.isArray(t.assignees) && t.assignees.includes(user.id));
    let base;
    if (!isManager || scope === "mine") {
      base = tasks.filter(belongsToUser).concat(todosAsTasks);
    } else {
      // scope === "assigned" — tasks the manager created for other people
      base = tasks.filter((t) => t.assigned_by === user.id && t.assigned_to !== user.id);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return base;
    return base.filter((t) => {
      const hay = [
        t.title, t.client, t.description, t.hold_reason, t.incomplete_reason,
        ...(Object.values(t.assignee_names || {})),
        t.assigned_to_name,
      ].filter(Boolean).join(" \u0001 ").toLowerCase();
      return hay.includes(q);
    });
  }, [tasks, todosAsTasks, user, isManager, scope, searchQuery]);

  const now = new Date();
  const buckets = useMemo(() => {
    const dS = startOfDay(now), dE = endOfDay(now);
    // Tomorrow (single day)
    const tS = new Date(dS); tS.setDate(tS.getDate() + 1);
    const tE = endOfDay(tS);
    const wS = startOfWeek(now), wE = endOfWeek(now);
    // Upcoming week (Mon–Sun after the current one)
    const uS = new Date(wS); uS.setDate(uS.getDate() + 7);
    const uE = new Date(wE); uE.setDate(uE.getDate() + 7);
    const mS = startOfMonth(now), mE = endOfMonth(now);
    // A task is "in bucket" if today ∈ [start_date, deadline] OR its deadline falls in the bucket.
    const inWindow = (t, from, to) => {
      const s = t.start_date ? new Date(t.start_date) : (t.deadline ? new Date(t.deadline) : null);
      const e = t.deadline ? new Date(t.deadline) : s;
      if (!s && !e) return false;
      return e >= from && s <= to;
    };
    return {
      today:    scopedTasks.filter((t) => inWindow(t, dS, dE)),
      tomorrow: scopedTasks.filter((t) => inWindow(t, tS, tE)),
      week:     scopedTasks.filter((t) => inWindow(t, wS, wE)),
      upcoming: scopedTasks.filter((t) => inWindow(t, uS, uE)),
      month:    scopedTasks.filter((t) => inWindow(t, mS, mE)),
      up_next:  scopedTasks.filter((t) => !t.start_date && !t.deadline),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedTasks]);

  const mineCount = useMemo(
    () => (user ? tasks.filter((t) => t.assigned_to === user.id).length : 0),
    [tasks, user]
  );
  const assignedByMeCount = useMemo(
    () => (user ? tasks.filter((t) => t.assigned_by === user.id && t.assigned_to !== user.id).length : 0),
    [tasks, user]
  );

  const openEdit = (t) => { setEditing(t); setOpen(true); };

  const setStatus = async (task, newStatus, { hold_reason = "", incomplete_reason = "" } = {}) => {
    try {
      // Special-case: todo-derived pseudo-tasks. There's no /api/tasks/{id} for
      // them — we just toggle the underlying to-do's `done` flag.
      if (task._from_todo) {
        const done = newStatus === "complete";
        await api.patch(`/todos/${task._todo_id}`, { done });
        setTodos((prev) => prev.map((x) => (x.id === task._todo_id ? { ...x, done } : x)));
        return;
      }
      const payload = { status: newStatus };
      if (newStatus === "on_hold") payload.hold_reason = hold_reason;
      if (newStatus === "incomplete") payload.incomplete_reason = incomplete_reason;
      // Clear the "other" reason to keep card clean when transitioning out of that state
      if (newStatus !== "on_hold" && task.hold_reason) payload.hold_reason = "";
      if (newStatus !== "incomplete" && task.incomplete_reason) payload.incomplete_reason = "";
      // Use the server response instead of the optimistic local merge — the
      // backend re-derives the rollup `status` from per_assignee_status and
      // returns the fresh `assignee_details` map. Merging just `payload`
      // caused a visual "revert" on multi-assignee tasks where the rollup
      // stayed at in_progress even though the caller's own slot went complete.
      const { data } = await api.patch(`/tasks/${task.id}`, payload);
      setTasks((prev) => prev.map((x) => (x.id === task.id ? data : x)));
      // Any transition INTO Complete or Carry-forward → pop the wrap-up
      // dialog to log today's hours. Carry-forward hides the delivery-links
      // section (nothing shipped yet).
      const nowFinishing = ["complete", "carry_forward"].includes(newStatus);
      if (nowFinishing && task.status !== newStatus) {
        setCompletePopupTask({ ...data, __popup_mode: newStatus });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update status");
    }
  };

  const renderGroup = (list, empty) => (
    list.length === 0 ? (
      <EmptyState text={empty} actionLabel="Add task" onAction={() => { setEditing(null); setOpen(true); }} testid="my-tasks-empty" />
    ) : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((t) => (
          <TaskCard
            key={t.id}
            task={t}
            currentUser={user}
            users={users}
            isManager={isManager}
            onEdit={t._from_todo ? undefined : openEdit}
            onStatusChange={setStatus}
          />
        ))}
      </div>
    )
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="overline flex items-center gap-2"><ListTodo size={12} /> My Tasks</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1 mb-6">Focus mode</h1>
        </div>
        {/* Everyone can create a task now — non-managers get a self-locked dialog. */}
        <Button
          data-testid="my-tasks-add-task-btn"
          onClick={() => { setEditing(null); setOpen(true); }}
          className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full"
        >
          <Plus size={16} className="mr-1" /> {isManager ? "New Task" : "Add Task for Yourself"}
        </Button>
      </div>

      {/* Search — free-text across title / client / description / assignees */}
      <div className="mb-3 relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search title, client, notes…"
          data-testid="my-tasks-search"
          className="w-full h-9 bg-black/40 border border-white/10 rounded-full pl-9 pr-8 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-red-500"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            data-testid="my-tasks-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Scope toggle — managers/super admins see both "Mine" and "Assigned by me" */}
      {isManager && (
        <div className="mb-4 inline-flex rounded-full border border-white/10 p-1 bg-black/40" data-testid="my-tasks-scope">
          <button
            onClick={() => setScope("mine")}
            data-testid="my-tasks-scope-mine"
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${
              scope === "mine" ? "bg-[#E11D2E]/20 text-white border border-[#E11D2E]/40" : "text-white/60 hover:text-white"
            }`}
          >
            <UserCircle2 size={13} />
            My work · {mineCount}
          </button>
          <button
            onClick={() => setScope("assigned")}
            data-testid="my-tasks-scope-assigned"
            className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs uppercase tracking-widest transition-colors ${
              scope === "assigned" ? "bg-[#E11D2E]/20 text-white border border-[#E11D2E]/40" : "text-white/60 hover:text-white"
            }`}
          >
            <Share2 size={13} />
            Assigned by me · {assignedByMeCount}
          </button>
        </div>
      )}

      <Tabs defaultValue="today" className="w-full">
        <TabsList className="bg-surface border border-white/5 flex-wrap h-auto">
          <TabsTrigger value="today"    className="data-[state=active]:bg-white/10">Today ({buckets.today.length})</TabsTrigger>
          <TabsTrigger value="tomorrow" className="data-[state=active]:bg-white/10">Tomorrow ({buckets.tomorrow.length})</TabsTrigger>
          <TabsTrigger value="week"     className="data-[state=active]:bg-white/10">This Week ({buckets.week.length})</TabsTrigger>
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-white/10">Upcoming Week ({buckets.upcoming.length})</TabsTrigger>
          <TabsTrigger value="month"    className="data-[state=active]:bg-white/10">This Month ({buckets.month.length})</TabsTrigger>
          <TabsTrigger value="up_next"  className="data-[state=active]:bg-white/10">Up Next ({buckets.up_next.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4">
          {renderGroup(buckets.today, scope === "assigned" ? "You haven't assigned anything due today." : "Nothing due today. Enjoy the calm.")}
        </TabsContent>
        <TabsContent value="tomorrow" className="mt-4">
          {renderGroup(buckets.tomorrow, scope === "assigned" ? "You haven't assigned anything due tomorrow." : "Tomorrow is free.")}
        </TabsContent>
        <TabsContent value="week" className="mt-4">
          {renderGroup(buckets.week, scope === "assigned" ? "Nothing you assigned is due this week." : "No tasks this week.")}
        </TabsContent>
        <TabsContent value="upcoming" className="mt-4">
          {renderGroup(buckets.upcoming, scope === "assigned" ? "Nothing you assigned is due next week." : "Next week is empty so far.")}
        </TabsContent>
        <TabsContent value="month" className="mt-4">
          {renderGroup(buckets.month, scope === "assigned" ? "Nothing you assigned is due this month." : "No tasks this month.")}
        </TabsContent>
        <TabsContent value="up_next" className="mt-4">
          {renderGroup(buckets.up_next, "Everything is scheduled — no un-dated tasks parked here.")}
        </TabsContent>
      </Tabs>

      <div className="mt-8">
        <NotesTodoInline />
      </div>

      <TaskDialog
        open={open}
        onOpenChange={setOpen}
        users={users}
        existing={editing}
        onSaved={load}
        onTaskCompleted={(t) => setCompletePopupTask(t)}
      />
      <CompleteLinksDialog
        open={!!completePopupTask}
        task={completePopupTask}
        onClose={(updated) => {
          if (updated) setTasks((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
          setCompletePopupTask(null);
        }}
      />
    </div>
  );
}
